import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, BehaviorSubject, of } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import * as msal from '@azure/msal-browser';
import { ApiConfigService } from './api-config.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class MsalAuthService implements OnDestroy {
  private msalInstance!: msal.PublicClientApplication;
  private loggedIn = new BehaviorSubject<boolean>(false);
  private initialized = false;
  private tokenExpiryCheckInterval: any;
  private cachedToken: string | null = null;
  private tokenExpiry: number | null = null;
  private initializationPromise: Promise<void> | null = null;
  private interactionInProgress = false;
  private loginQueue: Array<{resolve: Function, reject: Function, redirectPath?: string}> = [];
  private processingQueue = false;

  private readonly msalConfig: msal.Configuration = {
    auth: {
      clientId: 'f90f242d-2c9e-49f5-ab2c-d4c298a960f4',
      authority: 'https://login.microsoftonline.com/49c94ec6-f81e-4f42-8e4b-ed303e0dbec8',
      redirectUri: this.apiConfig.authRedirectUri,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case msal.LogLevel.Error:
              console.error('[MSAL]', message);
              break;
            case msal.LogLevel.Info:
              console.info('[MSAL]', message);
              break;
            case msal.LogLevel.Verbose:
              console.debug('[MSAL]', message);
              break;
            case msal.LogLevel.Warning:
              console.warn('[MSAL]', message);
              break;
          }
        },
        logLevel: msal.LogLevel.Verbose,
      }
    }
  };

  private readonly loginRequest = {
    scopes: ['openid', 'profile', 'email', 'User.Read', 'offline_access'],
    prompt: 'select_account'
  };

  constructor(
    private http: HttpClient,
    private apiConfig: ApiConfigService,
    private router: Router
  ) {
    this.setupTokenExpiryCheck();
    this.initializationPromise = this.handleInitialization();
  }

  private setupTokenExpiryCheck(): void {
    if (this.tokenExpiryCheckInterval) {
      clearInterval(this.tokenExpiryCheckInterval);
    }
    
    this.tokenExpiryCheckInterval = setInterval(() => {
      if (this.tokenExpiry && Date.now() >= this.tokenExpiry - 300000) { // 5 minutes before expiry
        this.refreshToken();
      }
    }, 60000);
  }

  private async handleInitialization(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      console.log('Initializing MSAL...');
      this.msalInstance = new msal.PublicClientApplication(this.msalConfig);
      await this.msalInstance.initialize();
      this.initialized = true;
      console.log('MSAL initialized successfully');

      // Add a flag to detect if there's already a redirect in progress
      this.checkRedirectInProgress();

      // Handle redirect response only if we have a hash in the URL
      if (window.location.hash && window.location.hash.includes('code=')) {
        const response = await this.msalInstance.handleRedirectPromise();
        if (response) {
          await this.handleAuthResponse(response);
        }
      }

      // Check for existing account
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(accounts[0]);
        this.loggedIn.next(true);
        await this.refreshToken();
      }

      await this.logAuthenticationStatus();
    } catch (error) {
      console.error('Error during auth initialization:', error);
      this.initialized = false;
      throw error;
    }
  }

  private async handleAuthResponse(response: msal.AuthenticationResult): Promise<void> {
    if (response.account) {
      this.msalInstance.setActiveAccount(response.account);
      this.loggedIn.next(true);
      this.updateTokenCache(response.accessToken);
      
      // Get the return URL from storage or default to harmonization-data
      const returnUrl = sessionStorage.getItem('loginRedirect') || '/harmonization-data';
      sessionStorage.removeItem('loginRedirect');
      await this.router.navigate([returnUrl]);
    }
  }

  public async handleRedirectCallback(): Promise<void> {
    try {
      await this.ensureInitialized();
      const result = await this.msalInstance.handleRedirectPromise();
      if (result) {
        await this.handleAuthResponse(result);
      }
    } catch (error) {
      console.error('Error handling redirect:', error);
      this.loggedIn.next(false);
      throw error;
    }
  }

  private async refreshToken(): Promise<void> {
    try {
      const token = await this.acquireNewToken();
      if (token) {
        this.updateTokenCache(token);
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      if (error instanceof msal.InteractionRequiredAuthError) {
        await this.login();
      }
    }
  }

  private updateTokenCache(token: string): void {
    this.cachedToken = token;
    const tokenData = this.decodeToken(token);
    this.tokenExpiry = tokenData.exp * 1000; // Convert to milliseconds
  }

  /**
   * Check for any signs of redirect or interaction in progress
   * This is a safer approach than trying to access internal MSAL properties
   */
  private checkRedirectInProgress(): void {
    // First try to use MSAL's native API if available
    try {
      // Safely access the potentially internal method
      const msalInstance: any = this.msalInstance; // cast to any to bypass TypeScript checks
      if (msalInstance && typeof msalInstance.getInteractionStatus === 'function') {
        const status = msalInstance.getInteractionStatus();
        if (status && status !== 'None') {
          console.log(`MSAL reports interaction status: ${status}`);
          this.interactionInProgress = true;
          return;
        }
      }
    } catch (error) {
      console.warn('Error checking MSAL interaction status:', error);
    }

    // Check for a hash in the URL which indicates a redirect is in process
    if (window.location.hash && (
      window.location.hash.includes('code=') || 
      window.location.hash.includes('error=') || 
      window.location.hash.includes('state=')
    )) {
      console.log('Detected redirect parameters in URL hash');
      this.interactionInProgress = true;
      return;
    }
    
    // Check for existing data in localStorage that indicates an interaction
    try {
      const storageKeys = Object.keys(window.localStorage);
      const interactionKeys = storageKeys.filter(key => 
        key.includes('interaction.status') || 
        key.includes('MSAL.interaction') ||
        key.includes('msal.interaction')
      );
      
      if (interactionKeys.length > 0) {
        console.log('Detected interaction keys in localStorage:', interactionKeys);
        this.interactionInProgress = true;
        return;
      }
    } catch (e) {
      console.warn('Error checking localStorage:', e);
    }
  }

  /**
   * Clear any stuck interactions to reset the auth state
   * This is a last-resort method to clear a stuck interaction
   */
  private clearStuckInteractions(): void {
    try {
      console.log('Clearing stuck MSAL interactions');
      
      // Clear all interaction-related entries in localStorage
      const storageKeys = Object.keys(window.localStorage);
      const interactionKeys = storageKeys.filter(key => 
        key.includes('interaction.status') || 
        key.includes('MSAL.interaction') ||
        key.includes('msal.interaction') ||
        key.includes('msal.cache')
      );
      
      for (const key of interactionKeys) {
        console.log(`Removing stuck interaction: ${key}`);
        localStorage.removeItem(key);
      }
      
      // Also try to clear sessionStorage
      const sessionKeys = Object.keys(window.sessionStorage);
      const msalSessionKeys = sessionKeys.filter(key => 
        key.includes('msal') || 
        key.includes('MSAL') ||
        key.includes('interaction')
      );
      
      for (const key of msalSessionKeys) {
        sessionStorage.removeItem(key);
      }
      
      // Reset the interaction flags
      this.interactionInProgress = false;
      
      // If there is a cached instance of MSAL, try to handle any pending redirect
      if (this.msalInstance && this.initialized) {
        // Make sure any pending redirects are handled
        this.msalInstance.handleRedirectPromise().catch(error => {
          console.log('Error handling redirect during cleanup:', error);
        });
      }
    } catch (error) {
      console.error('Error clearing stuck interactions:', error);
      // Even if there's an error, reset the flag
      this.interactionInProgress = false;
    }
  }

  async login(redirectPath?: string): Promise<void> {
    // Wait a tiny bit to allow any existing checks to complete 
    // This helps with the race condition between components
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // First check for any stuck interactions before attempting login
    try {
      const storageKeys = Object.keys(window.localStorage);
      const interactionKeys = storageKeys.filter(key => 
        key.includes('interaction.status') || 
        key.includes('MSAL.interaction')
      );
      
      if (interactionKeys.length > 0) {
        // Check if interaction appears to be stuck (older than 5 minutes)
        const now = Date.now();
        let isStuck = false;
        
        for (const key of interactionKeys) {
          try {
            const value = JSON.parse(localStorage.getItem(key) || '{}');
            if (value.ts && (now - value.ts > 5 * 60 * 1000)) {
              console.log(`Found stuck interaction from ${new Date(value.ts).toLocaleString()}`);
              isStuck = true;
              break;
            }
          } catch (parseError) {
            // If we can't parse it, assume it's stuck
            isStuck = true;
          }
        }
        
        if (isStuck) {
          console.log('Detected stuck interaction, clearing...');
          this.clearStuckInteractions();
        }
      }
    } catch (error) {
      console.warn('Error checking for stuck interactions:', error);
    }
    
    // Check for any signs of redirect/interaction in progress
    this.checkRedirectInProgress();
    
    // First check if we're already in an interaction before queueing
    if (this.interactionInProgress) {
      console.log('Login interaction already in progress, waiting for it to complete');
      return Promise.reject(new msal.BrowserAuthError('interaction_in_progress', 'Interaction already in progress'));
    }
    
    // Return a new promise that will be resolved when login completes
    return new Promise<void>(async (resolve, reject) => {
      // Add this login request to the queue
      this.loginQueue.push({ resolve, reject, redirectPath });
      
      // If we're already processing the queue, just return and wait
      if (this.processingQueue) {
        console.log('Login queue processing already in progress, waiting in queue');
        return;
      }
      
      // Start processing the queue
      await this.processLoginQueue();
    });
  }

  private async processLoginQueue(): Promise<void> {
    if (this.processingQueue || this.loginQueue.length === 0) {
      return;
    }
    
    this.processingQueue = true;
    
    try {
      console.log(`Processing login queue. Queue length: ${this.loginQueue.length}`);
      
      // Double-check for interaction in progress before proceeding
      this.checkRedirectInProgress();
      
      if (this.interactionInProgress) {
        console.log('Interaction already in progress, rejecting queued requests');
        // Reject all current queue items with interaction_in_progress
        while (this.loginQueue.length > 0) {
          const request = this.loginQueue.shift();
          if (request) {
            request.reject(new msal.BrowserAuthError('interaction_in_progress', 'Interaction already in progress'));
          }
        }
        return;
      }
      
      // Process all queued login requests
      while (this.loginQueue.length > 0 && !this.interactionInProgress) {
        const { resolve, reject, redirectPath } = this.loginQueue[0];
        
        try {
          console.log('Initiating login...');
          await this.ensureInitialized();
          
          if (this.interactionInProgress) {
            console.log('Interaction already in progress, waiting...');
            break; // Exit the loop but keep the request in the queue
          }
          
          this.interactionInProgress = true;
          
          // Set a safety timeout to clear the flag after 2 minutes
          setTimeout(() => {
            if (this.interactionInProgress) {
              console.log('Safety timeout: Clearing interactionInProgress flag');
              this.interactionInProgress = false;
              this.processLoginQueue(); // Try processing the queue again
            }
          }, 120000);
          
          if (redirectPath) {
            sessionStorage.setItem('loginRedirect', redirectPath);
          }

          // Register event handler to reset interactionInProgress flag
          const callbackId = this.msalInstance.addEventCallback((event) => {
            if (event.eventType === msal.EventType.LOGIN_SUCCESS || 
                event.eventType === msal.EventType.LOGIN_FAILURE || 
                event.eventType === msal.EventType.SSO_SILENT_SUCCESS || 
                event.eventType === msal.EventType.SSO_SILENT_FAILURE) {
              console.log('MSAL auth event completed:', event.eventType);
              this.interactionInProgress = false;
              
              // Remove this specific callback to prevent memory leaks
              if (callbackId) {
                this.msalInstance.removeEventCallback(callbackId);
              }
              
              // Resume queue processing
              setTimeout(() => this.processLoginQueue(), 500);
            }
          });

          await this.msalInstance.loginRedirect({
            ...this.loginRequest,
            redirectStartPage: window.location.href
          });
          
          // The redirect will happen here, so we won't reach the next line until
          // we come back from the redirect. At that point, the callback will have
          // already handled the response and we should just resolve this promise.
          resolve();
        } catch (error) {
          console.error('Error during login:', error);
          this.interactionInProgress = false;
          reject(error);
        }
        
        // Remove this request from the queue regardless of success/failure
        this.loginQueue.shift();
      }
    } finally {
      this.processingQueue = false;
      
      // If there are still items in the queue and no interaction is in progress,
      // start processing the queue again after a short delay
      if (this.loginQueue.length > 0 && !this.interactionInProgress) {
        setTimeout(() => this.processLoginQueue(), 500);
      }
    }
  }

  async logout(): Promise<void> {
    try {
      await this.ensureInitialized();
      
      // Clear local token cache
      this.cachedToken = null;
      this.tokenExpiry = null;
      this.loggedIn.next(false);
      
      // Use the current origin for postLogoutRedirectUri to ensure proper environment redirection
      const logoutRequest: msal.EndSessionRequest = {
        postLogoutRedirectUri: window.location.origin
      };
      
      await this.msalInstance.logoutRedirect(logoutRequest);
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback to manual redirect on logout failure
      window.location.href = window.location.origin;
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.handleInitialization();
    }
  }

  public async getToken(): Promise<string | null> {
    await this.ensureInitialized();

    if (this.cachedToken && !this.isTokenExpired()) {
      return this.cachedToken;
    }

    try {
      const token = await this.acquireNewToken();
      if (token) {
        this.updateTokenCache(token);
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error acquiring token:', error);
      return null;
    }
  }

  private async acquireNewToken(): Promise<string | null> {
    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      console.log('No active account found when requesting token');
      throw new Error('No active account!');
    }

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        ...this.loginRequest,
        account
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof msal.InteractionRequiredAuthError) {
        console.log('Interaction required for token acquisition');
        await this.login();
      }
      throw error;
    }
  }

  isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  isInteractionInProgress(): boolean {
    return this.interactionInProgress;
  }

  ngOnDestroy() {
    if (this.tokenExpiryCheckInterval) {
      clearInterval(this.tokenExpiryCheckInterval);
    }
  }

  private isTokenExpired(): boolean {
    return this.tokenExpiry ? Date.now() >= this.tokenExpiry : true;
  }

  private async logAuthenticationStatus() {
    try {
      const accounts = this.msalInstance.getAllAccounts();
      console.log('\n=== MSAL Authentication Status ===');
      console.log('Initialized:', this.initialized);
      console.log('Number of Accounts:', accounts.length);

      if (accounts.length > 0) {
        const activeAccount = this.msalInstance.getActiveAccount();
        if (activeAccount) {
          console.log('\nActive Account:', {
            username: activeAccount.username,
            name: activeAccount.name,
            environment: activeAccount.environment
          });

          const token = await this.getToken();
          if (token) {
            const tokenInfo = this.decodeToken(token);
            console.log('\nToken Information:', {
              expiration: new Date(tokenInfo.exp * 1000).toLocaleString(),
              issuedAt: new Date(tokenInfo.iat * 1000).toLocaleString(),
              scopes: tokenInfo.scp || tokenInfo.scope
            });
          }
        }
      }
      console.log('Authentication State:', this.loggedIn.value);
      console.log('===============================\n');
    } catch (error) {
      console.error('Error logging authentication status:', error);
    }
  }

  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    } catch (error) {
      console.error('Error decoding token:', error);
      return {};
    }
  }
}
